import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from "@stencil/core";
import { ToastMessageAnimationEnum, ToastMessageCategoryEnum } from "./toast-message.enum";

const baseClass = "ux-toast-message";

@Component({
    tag: "ux-toast-message",
    styleUrl: "toast-message.scss",
    shadow: true
})
export class ToastMessage {
    /**
     * The underlying HTML element of the toast message component.
     */
    @Element() element: HTMLUxToastMessageElement;

    /**
     * An identifier for the timeout that will remove the toast message from the view. This is managed
     * internally and can be cleared if the toast is manually dismissed before the timeout.
     * @private
     */
    private removeTimeout: number;

    /**
     * The category of the toast message, determining the icon and style applied. Defaults to 'Info'.
     * Reflects to an attribute for styling.
     */
    @Prop({ reflect: true }) readonly category: keyof typeof ToastMessageCategoryEnum | ToastMessageCategoryEnum =
        ToastMessageCategoryEnum.Info;

    /**
     * The lifetime of the toast message in milliseconds. If greater than 0, the toast will auto-expire
     * after this duration.
     */
    @Prop() readonly lifetime: number = 0;

    /**
     * The message text displayed to the user. This is the content of the toast notification.
     */
    @Prop() readonly message: string;

    /**
     * The duration for the transition animation of the toast message in milliseconds. Controls the
     * appearance and disappearance speed of the toast.
     */
    @Prop() readonly transitionDuration: number = 200;

    /**
     * The internal animation phase of the toast message, managing the toast's transition states.
     * Reflects to an attribute for CSS transitions.
     */
    @Prop({ mutable: true, reflect: true }) transitionPhase: ToastMessageAnimationEnum = ToastMessageAnimationEnum.Initial;

    /**
     * Event that is emitted when the message is removed from the stack.
     */
    @Event({ eventName: "uxRemove", bubbles: true })
    removeEvent: EventEmitter;

    /**
     * Public method that can be called to close the message.
     */
    @Method()
    public async close(): Promise<void> {
        this.handleRemove();
    }

    @Watch("transitionPhase")
    handlePhase() {
        window.clearTimeout(this.removeTimeout);
        switch (this.transitionPhase) {
            case ToastMessageAnimationEnum.Initial:
                window.setTimeout(() => (this.transitionPhase = ToastMessageAnimationEnum.Appear));
                break;
            case ToastMessageAnimationEnum.Appear:
                window.setTimeout(() => (this.transitionPhase = ToastMessageAnimationEnum.Idle), this.transitionDuration);
                break;
            case ToastMessageAnimationEnum.Idle:
                if (this.lifetime > 0) {
                    this.removeTimeout = window.setTimeout(this.handleRemove, this.lifetime);
                }
                break;
            case ToastMessageAnimationEnum.Disappear:
                window.setTimeout(() => {
                    if (this.element.parentElement) {
                        this.element.parentElement.removeChild(this.element);
                    }
                }, this.transitionDuration);
                break;
            default:
                break;
        }
    }

    componentWillLoad() {
        this.element.style.setProperty("--ux-toast-message-transition-duration", `${this.transitionDuration}ms`);
    }

    componentDidLoad() {
        this.handlePhase();
    }

    private handleRemove = () => {
        this.removeEvent.emit();
        this.transitionPhase = ToastMessageAnimationEnum.Disappear;
    };

    private renderIcon = () => {
        switch (this.category) {
            case ToastMessageCategoryEnum.Success:
                return <ux-icon name="checkmark" />;
            case ToastMessageCategoryEnum.Warning:
                return <ux-icon name="triangle-warning" weight="bold" />;
            case ToastMessageCategoryEnum.Error:
                return <ux-icon name="cross" />;
            case ToastMessageCategoryEnum.Info:
            default:
                return <ux-icon name="circle-info" weight="bold" />;
        }
    };

    render() {
        return (
            <Host>
                <div class={`${baseClass}__inner`}>
                    <div class={`${baseClass}__icon`}>{this.renderIcon()}</div>
                    <div class={`${baseClass}__message`}>
                        {this.message}
                        <div>
                            <slot />
                        </div>
                    </div>
                    {this.transitionPhase === ToastMessageAnimationEnum.Idle && (
                        <button class={`${baseClass}__remove`} onClick={this.handleRemove}>
                            <ux-icon name="cross" />
                        </button>
                    )}
                </div>
            </Host>
        );
    }
}
